import { Download, Edit, Loader2, Mail, PlayCircle } from "lucide-react";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import api from "~/services/api";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { AlertContext } from "~/App";

// import { Container } from './styles';

function TransferEligibility({
  enrollment = null,
  access = null,
  student_id = null,
  setLoading = () => null,
  loading = false,
  handleStartProcess = null,
  hideStatusHeader = false,
  hideStudentActions = false,
}) {
  const { alertBox } = useContext(AlertContext);
  const accesses = useSelector((state) => state.auth.accesses);
  const canSendAcceptanceLetter = Boolean(
    accesses?.hierarchy
      ?.find((menu) => menu.alias === "commercial")
      ?.children?.find((menu) => menu.alias === "enrollments")
      ?.children?.some((permission) =>
        permission.alias === "send-transfer-acceptance-letter-tab" &&
        permission.MenuHierarchyXGroup?.view === true
      )
  );
  const generationMenu = access?.children?.find(child => child.alias === "generate-atypical-transfer-acceptance-letter");
  const generationAccess = generationMenu?.MenuHierarchyXGroup || generationMenu || {};
  const canViewLetters = canSendAcceptanceLetter || generationAccess.view === true;
  const canManageLetters = canSendAcceptanceLetter;
  const canGenerateLetters = generationAccess.view === true && generationAccess.edit === true;
  const canDownloadLetters = canManageLetters || canGenerateLetters;
  const [letters, setLetters] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [letterBusy, setLetterBusy] = useState(false);
  const [showLetterDate, setShowLetterDate] = useState(false);
  const [acceptanceDate, setAcceptanceDate] = useState("");
  const [historyError, setHistoryError] = useState("");
  const loadLetters = useCallback(async () => {
    if (!enrollment?.id || !canViewLetters) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const { data } = await api.get(`/enrollments/${enrollment.id}/transfer-acceptance-letters`);
      setLetters(data);
    } catch (err) {
      setHistoryError(err?.response?.data?.error || "Could not load letter history.");
    } finally { setHistoryLoading(false); }
  }, [enrollment?.id, canViewLetters]);
  useEffect(() => { setLetters([]); setShowLetterDate(false); setAcceptanceDate(""); loadLetters(); }, [loadLetters]);

  async function downloadLetter(letterId) {
    const { data } = await api.get(`/enrollments/${enrollment.id}/transfer-acceptance-letters/${letterId}/pdf`, { responseType: "blob" });
    saveAs(new Blob([data], { type: "application/pdf" }), `transfer_acceptance_${letterId}.pdf`);
  }

  async function handleDownloadLetter(letterId) {
    if (!canDownloadLetters) return;
    setLetterBusy(true);
    try { await downloadLetter(letterId); }
    catch { toast.error("Could not download the letter."); }
    finally { setLetterBusy(false); }
  }

  function handleDSOTransferAccepted() {
    if (!canGenerateLetters) return;
    alertBox({
      title: "Issue Standalone Letter",
      descriptionHTML: enrollment?.enrollmenttransfers?.student_signature && enrollment?.enrollmenttransfers?.dso_signature
        ? "Do you want to create a letter outside the transfer process?"
        : "The transfer process has not been finalized yet. Do you want to create a letter outside the process?",
      buttons: [
        { title: "No", class: "cancel" },
        { title: "Yes", onPress: () => { setAcceptanceDate(""); setShowLetterDate(true); } },
      ],
    });
  }

  async function handleCreateLetter() {
    if (!canGenerateLetters || !acceptanceDate) return;
    setLetterBusy(true);
    let letter;
    try {
      const { data } = await api.post(`/enrollments/${enrollment.id}/transfer-acceptance-letters`, { start_date: acceptanceDate });
      letter = data;
      setShowLetterDate(false);
      toast.success("Letter created and saved in history.");
      await loadLetters();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create the letter.");
    }
    if (letter) {
      try { await downloadLetter(letter.id); }
      catch { toast.error("Letter saved. Download it from the history below."); }
    }
    setLetterBusy(false);
  }

  const process = `Transfer Eligibility`;
  const routine = `transfer-eligibility`;

  function handleSendMail() {
    setLoading(true);
    api
      .post(`/enrollments/send-form-mail`, {
        type: routine,
        enrollment_id: enrollment.id,
        student_id,
      })
      .then(({ data }) => {
        setLoading(false);
        toast("Form mail sent!", { autoClose: 1000 });
      });
  }

  function handleOpenDSOForm() {
    setLoading(true);
    api
      .get(`/enrollmenttransfer/dso-link/${enrollment.id}`)
      .then(({ data }) => {
        window.open(data.link, "_blank");
      })
      .catch((err) => {
        toast(err?.response?.data?.error || "DSO link not available.", {
          type: "error",
          autoClose: 3000,
        });
      })
      .finally(() => setLoading(false));
  }

  function handleSendMailToDSO() {
    setLoading(true);
    api
      .post(`/enrollmenttransfer/send-mail-to-dso`, {
        enrollment_id: enrollment.id,
        enrollmenttransfer_id: enrollment?.enrollmenttransfers.id,
      })
      .then(({ data }) => {
        setLoading(false);
        toast("Form mail sent!", { autoClose: 1000 });
      });
  }

  function handleOpenTransferEligibility() {
    setLoading(true);
    api
      .get(`/PDF/transfer-eligibility/${enrollment.id}`, {
        responseType: "blob",
      })
      .then((res) => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        saveAs(pdfBlob, `transfer_eligibility_${enrollment.id}.pdf`);
      })
      .catch((err) => {
        toast("Transfer Eligibility not available yet.", {
          type: "error",
          autoClose: 3000,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const started = enrollment?.enrollmenttransfers ? true : false;
  const studentSigned = enrollment?.enrollmenttransfers?.student_signature
    ? true
    : false;
  const dsoSigned = enrollment?.enrollmenttransfers?.dso_signature
    ? true
    : false;

  if (!enrollment) {
    return (
      <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
        <div className="flex w-96 flex-col items-center justify-center text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
          {loading ? (
            <div className="w-full bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-between text-xs gap-1">
              <strong>Please wait...</strong>
              <Loader2 className="animate-spin" size={14} />
            </div>
          ) : (
            <button
              type="button"
              disabled={loading}
              onClick={() => handleStartProcess(routine)}
              className="w-full bg-green-300 text-green-800 border border-green-400 hover:bg-green-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-between text-xs gap-1"
            >
              <strong>Start {process} Process</strong>
              <PlayCircle size={18} />
            </button>
          )}
          <p className="text-xs text-gray-500 text-left px-2 border-t pt-4">
            This action will send an email to the student with a link to
            complete the {process} form.
          </p>
        </div>
      </div>
    );
  }

  const transferCompleted = studentSigned && dsoSigned;
  const latestProcessLetter = letters.find(letter => !letter.is_atypical) || null;

  function requireCompletedProcess() {
    if (transferCompleted) return true;
    alertBox({
      title: "Transfer process incomplete",
      descriptionHTML: "The transfer process has not been finalized. Both the Student and DSO must sign the Transfer Form before generating or sending the process acceptance letter.",
      buttons: [{ title: "OK", class: "cancel" }],
    });
    return false;
  }

  async function handleDownloadProcessLetter() {
    if (!canManageLetters || !requireCompletedProcess()) return;
    setLetterBusy(true);
    try {
      const { data } = await api.get(`/acceptance-letter/pdf/${enrollment.id}`, { responseType: "blob" });
      saveAs(new Blob([data], { type: "application/pdf" }), `acceptance_letter_${enrollment.id}.pdf`);
      await loadLetters();
    } catch (err) {
      let detail = err?.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text()); } catch { detail = null; }
      }
      toast.error(detail?.error || "Could not generate the process acceptance letter.");
    } finally { setLetterBusy(false); }
  }

  async function handleSendAcceptanceLetter(letter = null) {
    if (!canManageLetters) return;
    if (!letter?.is_atypical && !requireCompletedProcess()) return;
    alertBox({
      title: "Attention!",
      descriptionHTML: letter?.is_atypical
        ? `You are about to send a letter outside the complete transfer process, which normally requires the Student and DSO signatures. This saved letter uses the DSO-defined start date <strong>${letter.start_date}</strong>, ignoring the Transfer Form release date. Do you want to send this letter to the student and the configured recipients?`
        : "Are you sure you want to send the acceptance letter? This action cannot be undone.",
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            setLoading(true);
            try {
              await api.post(`/send-acceptance-letter/${enrollment.id}`, {
                origin: "Transfer",
                ...(letter?.id ? { transfer_acceptance_letter_id: letter.id } : {}),
              });
              toast("Acceptance letter sent successfully!", {
                autoClose: 1000,
              });
              await loadLetters();
              setLoading(false);
            } catch (err) {
              setLoading(false);
              if (err?.response?.data?.error) {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              }
            }
          },
        },
      ],
    });
  }

  return (
    <div className="flex flex-1 w-full flex-col items-start justify-start text-center gap-4 px-4">
      <div className="flex w-full flex-col items-start justify-start text-center gap-4 border border-gray-200 bg-slate-50 rounded-md p-4">
        {!hideStatusHeader && (
          <h2 className="text-md font-bold w-full text-left border-b border-gray-200 pb-2">
            Transfer Process
            {!started ? (
              <span className="text-red-500"> - Not started</span>
            ) : !studentSigned ? (
              <span className="text-orange-500">
                {" "}
                - Not signed by Student yet
              </span>
            ) : !dsoSigned ? (
              <span className="text-orange-500">
                {" "}
                - Signed by Student - Not signed by DSO
              </span>
            ) : (
              <span className="text-green-500">
                {" "}
                - Signed by Student - Signed by DSO
              </span>
            )}
          </h2>
        )}
        <div className="relative flex w-full flex-wrap flex-row items-center justify-start text-center gap-4">
          {!hideStudentActions && (
            <>
              <NavLink
                to={`/fill-form/Transfer?crypt=${enrollment.id}`}
                target="_blank"
                disabled={true}
                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
              >
                <Edit size={14} />
                <strong>Access the Student's Form</strong>
              </NavLink>
              <button
                type="button"
                disabled={loading}
                onClick={handleSendMail}
                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
              >
                <Mail size={14} />
                <strong>Re-send form link to student</strong>
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleSendMailToDSO}
                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
              >
                <Mail size={14} />
                <strong>Re-send mail to DSO</strong>
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={handleOpenDSOForm}
                className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
              >
                <Edit size={14} />
                <strong>Access the DSO's Form</strong>
              </button>
            </>
          )}
          {enrollment.form_step !== "transfer-request" && (
            <button
              type="button"
              disabled={loading}
              onClick={() => handleOpenTransferEligibility()}
              className="bg-mila_orange text-white border border-gray-800 hover:bg-gray-600 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
            >
              <Download size={14} />
              <strong>Transfer Eligibility Form</strong>
            </button>
          )}
          {canManageLetters && (
            <button type="button" disabled={loading || letterBusy || historyLoading}
              onClick={handleDownloadProcessLetter}
              className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 h-6 flex items-center text-xs gap-2">
              <Download size={14} /><strong>Download acceptance letter</strong>
            </button>
          )}
          {canManageLetters && (
            <button
              type="button"
              disabled={loading || letterBusy || historyLoading || !!historyError || !canManageLetters}
              onClick={() => handleSendAcceptanceLetter(latestProcessLetter)}
              className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white rounded-md py-4 px-4 my-2 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2"
            >
              <Mail size={14} />
              <strong>Send acceptance letter to student</strong>
            </button>
          )}
        </div>
        {canViewLetters && (
          <div className="w-full border-t pt-4 text-left">
            <h3 className="font-semibold text-sm mb-2">Generated Letters History</h3>
            {historyLoading ? <p className="text-xs">Loading...</p> : historyError ? (
              <div className="text-xs text-red-600">{historyError} <button type="button" onClick={loadLetters} className="underline">Retry</button></div>
            ) : letters.length === 0 ? <p className="text-xs text-gray-500">No letters generated yet.</p> : (
              <div className="overflow-x-auto"><table className="w-full text-xs">
                <thead><tr className="border-b"><th className="p-2 text-left">Generated at</th><th className="p-2 text-left">Start Date</th><th className="p-2 text-left">Type</th><th className="p-2 text-left">Generated by</th>{canDownloadLetters && <th className="p-2">Download</th>}{canManageLetters && <th className="p-2">Send</th>}</tr></thead>
                <tbody>{letters.map(letter => <tr key={letter.id} className="border-b">
                  <td className="p-2">{new Date(letter.created_at).toLocaleString()}</td>
                  <td className="p-2">{letter.start_date}</td>
                  <td className="p-2">{letter.is_atypical ? "Standalone letter" : "Transfer process"}</td>
                  <td className="p-2">{letter.createdBy?.name || "-"}</td>
                  {canDownloadLetters && <td className="p-2 text-center"><button type="button" disabled={letterBusy}
                    onClick={() => handleDownloadLetter(letter.id)} className="p-2 disabled:opacity-50" aria-label="Download letter"><Download size={14} /></button></td>}
                  {canManageLetters && <td className="p-2 text-center"><button type="button" disabled={loading || letterBusy}
                    onClick={() => handleSendAcceptanceLetter(letter)} className="p-2 disabled:opacity-50" aria-label="Send this letter"><Mail size={14} /></button></td>}
                </tr>)}</tbody>
              </table></div>
            )}
          </div>
        )}
        {canGenerateLetters && <div className="w-full border-t pt-4 text-left">
          <button type="button" disabled={loading || letterBusy} onClick={handleDSOTransferAccepted}
            className="bg-mila_orange text-white border border-gray-800 rounded-md py-2 px-4 flex items-center text-xs gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <Download size={14} /><strong>Issue Standalone Letter</strong>
          </button>
          <p className="mt-2 text-xs text-gray-500">Create a separate letter using a manually entered start date, independent of the Transfer Form.</p>
        </div>}
        {showLetterDate && canGenerateLetters && (
          <div className="flex w-full flex-wrap items-end gap-3 border-t pt-4 text-left">
            <label className="flex flex-col gap-1 text-xs">
              Student Acceptance Date (Start Date)
              <input type="date" value={acceptanceDate} onChange={(event) => setAcceptanceDate(event.target.value)}
                disabled={letterBusy} className="border rounded-md p-2 text-sm" />
            </label>
            <button type="button" disabled={!acceptanceDate || letterBusy} onClick={handleCreateLetter}
              className="bg-mila_orange text-white rounded-md px-4 py-2 text-xs disabled:opacity-50">
              {letterBusy ? "Creating..." : "Create and Download Letter"}
            </button>
            <button type="button" disabled={letterBusy} onClick={() => setShowLetterDate(false)} className="text-xs p-2">Cancel</button>
            <p className="w-full text-xs text-gray-500">This letter uses the date entered above, regardless of the Transfer Form release date.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransferEligibility;
